import { getToken, getTokenHeader } from "../Token";
import { toast } from "react-toastify";
import * as Sentry from '@sentry/react';
import {
  downloadFileInAndroid,
  downloadFileInBrowser,
} from "./downloadButtonUtils";

export const downloadAction = async (downloadUrl, queryParams, file_name) => {
  try {
    // Get authentication token
    let token = await getToken();

    const url = `${downloadUrl()}?${queryParams.toString()}`;

    // Fetch the file
    const response = await fetch(url, {
      method: "GET",
      ...getTokenHeader(token),
    });

    // Convert response to blob
    const blob = await response.blob();

    // Check if the response is an error message
    if (blob.type.includes("application/json")) {
      const displayName = file_name.replace(".pdf", "");
      toast.error(`No ${displayName} Found`);
      return Promise.reject(new Error(`No ${displayName} Found`));
    }

    // Detect if we're running in Android WebView
    const isAndroidWebView =
      /Android/i.test(navigator.userAgent) &&
      window.navigator.userAgent.includes("wv");

    // Handle download based on environment
    if (isAndroidWebView) {
      return downloadFileInAndroid(blob, file_name);
    } else {
      return downloadFileInBrowser(blob, file_name);
    }
  } catch (err) {
    console.error("Download error:", err);
    Sentry.captureException(err, {
      tags: { module: 'common', flow: 'downloadAction' },
      extra: { file_name },
    });
    toast.error("Download failed. Please try again.");
    return Promise.reject(err);
  }
};

/**
 * Common download handler — manages loading state, a minimum visible delay,
 * and error handling.
 *
 * @param {() => Promise} downloadFn  - Async function that performs the actual download
 * @param {Function} setIsLoading     - React setState setter for loading indicator
 * @param {string}  [showToast=null]   - Optional success message to display after download
 */
export const handleDownload = async (
  downloadFn,
  setIsLoading,
  showToast = null,
) => {
  const minDelay = 1000;

  try {
    setIsLoading(true);
    const minDelayPromise = new Promise((resolve) =>
      setTimeout(resolve, minDelay),
    );
    await Promise.all([downloadFn(), minDelayPromise]).then(() => {
      if (!!showToast) {
        toast.success(showToast);
      }
    });
  } catch (error) {
    console.error("Download failed:", error);
  } finally {
    setIsLoading(false);
  }
};